import { useMsal } from '@azure/msal-react'
import {
  ArrowUpDown,
  Building2,
  CalendarDays,
  Camera,
  Check,
  ChevronDown,
  ChevronRight,
  Clock,
  ExternalLink,
  GraduationCap,
  Home,
  Image,
  Loader2,
  LogOut,
  Mail,
  MapPin,
  Phone,
  Search,
  Settings,
  Shield,
  Trash2,
  User,
} from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { clearConfig } from '@/Config/ConfigService'
import { PoweredByFooter } from '@/components/PoweredByFooter'
import { ServiceBanner } from '@/components/ServiceBanner'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import { Separator } from '@/components/ui/separator'
import { useBanner } from '@/hooks/useBanner'
import { trackEvent } from '@/lib/analytics'
import { clearAppLocalStorage } from '@/lib/storage'

// ── Types ──────────────────────────────────────────────────────────────────

interface UserProfile {
  fullName: string
  informalName: string
  namePrefix: string
  organization: string
  status: string
  primaryEmail: string
  workPhone: string
  mobilePhone: string
  physicalFullAddress: string
  postalFullAddress: string
  createdOn: string
}

interface MyPracticeScreenProps {
  username?: string
  partyId?: string
  onNavigate?: (tab: string, subTab?: string) => void
  onSignOut?: () => void
}

interface SavedProperty {
  address: string
  rafid: number
  low: number
  high: number
  rating: number
  savedAt: string
}

// ── Helpers ────────────────────────────────────────────────────────────────

function getInitials(name: string): string {
  return name
    .split(' ')
    .filter(Boolean)
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

function formatAddress(raw: string): string {
  return raw.replace(/\r\n|\r|\n/g, ', ')
}

function getMapsUrl(raw: string): string {
  const query = encodeURIComponent(raw.replace(/\r\n|\r|\n/g, ' '))
  return `https://www.google.com/maps/search/?api=1&query=${query}`
}

function savePreference(userId: string, key: string, value: string): void {
  if (!userId) return
  fetch('api/user-preferences', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ userId, preferenceKey: key, preferenceValue: value }),
  }).catch(() => {})
}

interface SuburbLookup {
  suburbID: number
  suburbName: string
  taName: string | null
}

async function fetchSuburbSuggestions(query: string): Promise<SuburbLookup[]> {
  const res = await fetch(`/api/report/suburbs?query=${encodeURIComponent(query)}`)
  if (!res.ok) return []
  return res.json()
}

function detectSuburbName(address: string): string {
  const lines = address
    .split(/\r\n|\r|\n/)
    .map((l) => l.trim())
    .filter(Boolean)
  return lines[1] ?? ''
}

// ── Brand Image Card (shared across both tabs) ─────────────────────────────

function BrandImageCard({
  brandImageUrl,
  uploadingBrand,
  onUploadClick,
  onDelete,
}: {
  brandImageUrl: string | null
  uploadingBrand: boolean
  onUploadClick: () => void
  onDelete: () => void
}) {
  return (
    <Card className="p-4">
      <h3 className="font-semibold mb-1">Brand Image</h3>
      <p className="text-xs text-muted-foreground mb-4">
        Used on the cover of your shared Estimate and Market Insights reports.
      </p>
      {brandImageUrl ? (
        <div className="space-y-3">
          <div className="w-full h-24 rounded-lg border border-border overflow-hidden bg-muted flex items-center justify-center">
            <img src={brandImageUrl} alt="Brand" className="max-h-full max-w-full object-contain" />
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              className="flex-1"
              disabled={uploadingBrand}
              onClick={onUploadClick}
            >
              {uploadingBrand ? (
                <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
              ) : (
                <Image className="w-3.5 h-3.5 mr-1.5" />
              )}
              Replace
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="text-destructive hover:text-destructive"
              onClick={onDelete}
            >
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
      ) : (
        <Button
          variant="outline"
          className="w-full"
          disabled={uploadingBrand}
          onClick={onUploadClick}
        >
          {uploadingBrand ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Uploading…
            </>
          ) : (
            <>
              <Image className="w-4 h-4 mr-2" />
              Upload Brand Image
            </>
          )}
        </Button>
      )}
    </Card>
  )
}

// ── Component ──────────────────────────────────────────────────────────────

export function MyPracticeScreen({
  username = 'Agent',
  partyId,
  onNavigate,
  onSignOut,
}: MyPracticeScreenProps) {
  const banner = useBanner('myprofile')
  const { instance } = useMsal()

  const [activeSection, setActiveSection] = useState<'activity' | 'profile'>('activity')
  const [profile, setProfile] = useState<UserProfile | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [openPref, setOpenPref] = useState<'suburb' | 'privacy' | null>(null)
  const [defaultSuburb, setDefaultSuburb] = useState<SuburbLookup | null>(null)
  const [suburbQuery, setSuburbQuery] = useState('')
  const [suburbSuggestions, setSuburbSuggestions] = useState<SuburbLookup[]>([])
  const [suburbDropdownOpen, setSuburbDropdownOpen] = useState(false)
  const suburbDropdownRef = useRef<HTMLDivElement>(null)
  const [savedProperties, setSavedProperties] = useState<SavedProperty[]>([])
  const [savedEventCount, setSavedEventCount] = useState(0)
  const [savedEduCount, setSavedEduCount] = useState(0)
  const [activityLoading, setActivityLoading] = useState(false)
  const [profilePicUrl, setProfilePicUrl] = useState<string | null>(null)
  const [brandImageUrl, setBrandImageUrl] = useState<string | null>(null)
  const [uploadingPic, setUploadingPic] = useState(false)
  const [uploadingBrand, setUploadingBrand] = useState(false)
  const [tagline, setTagline] = useState('')
  const [taglineSaving, setTaglineSaving] = useState(false)
  const [picOffsetY, setPicOffsetY] = useState(50) // 0=top 100=bottom
  const [adjustingPic, setAdjustingPic] = useState(false)
  const profilePicInput = useRef<HTMLInputElement>(null)
  const brandImageInput = useRef<HTMLInputElement>(null)
  const taglineTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const dragStartY = useRef<number | null>(null)
  const dragStartOffset = useRef<number>(50)

  useEffect(() => {
    trackEvent('screen_viewed', { screen: 'my_practice' })
  }, [])

  useEffect(() => {
    if (!partyId) return
    const fetchProfile = async () => {
      setLoading(true)
      setError(null)
      try {
        const [profileRes, prefsRes] = await Promise.all([
          fetch(`api/profile/profiledata?query=${partyId}`),
          fetch(`api/user-preferences?userId=${encodeURIComponent(partyId)}`),
        ])

        const json = await profileRes.json()
        const data: UserProfile = json.data
        setProfile(data)

        const prefs: { preferenceKey: string; preferenceValue: string }[] = prefsRes.ok
          ? await prefsRes.json()
          : []

        // Suburb preference
        const pinnedSuburbRaw =
          prefs.find((p) => p.preferenceKey === 'reinz_pinned_suburb')?.preferenceValue ?? null
        if (pinnedSuburbRaw) {
          try {
            setDefaultSuburb(JSON.parse(pinnedSuburbRaw) as SuburbLookup)
          } catch {
            /* ignore */
          }
        } else if (data.physicalFullAddress) {
          const detectedName = detectSuburbName(data.physicalFullAddress)
          if (detectedName) {
            fetchSuburbSuggestions(detectedName)
              .then((results) => {
                const suburb = results[0] ?? null
                if (suburb) {
                  setDefaultSuburb(suburb)
                  savePreference(partyId, 'reinz_pinned_suburb', JSON.stringify(suburb))
                }
              })
              .catch(() => {})
          }
        }

        // Always save organisation — authoritative from iMIS
        if (data.organization) {
          savePreference(partyId, 'reinz_organisation', data.organization)
        }
      } catch (err: any) {
        setError(err.message ?? 'Failed to load profile')
      } finally {
        setLoading(false)
      }
    }
    fetchProfile()
  }, [partyId])

  useEffect(() => {
    if (!partyId) return
    setActivityLoading(true)
    Promise.all([
      fetch(`api/user-saved-items?userId=${encodeURIComponent(partyId)}&itemType=property`).then(
        (r) => r.json(),
      ),
      fetch(`api/user-saved-items?userId=${encodeURIComponent(partyId)}&itemType=event`).then((r) =>
        r.json(),
      ),
      fetch(`api/user-saved-items?userId=${encodeURIComponent(partyId)}&itemType=education`).then(
        (r) => r.json(),
      ),
    ])
      .then(([props, events, edu]) => {
        setSavedProperties(
          (props as any[])
            .map((i) => JSON.parse(i.itemData) as SavedProperty)
            .sort((a, b) => new Date(b.savedAt).getTime() - new Date(a.savedAt).getTime()),
        )
        setSavedEventCount((events as any[]).length)
        setSavedEduCount((edu as any[]).length)
      })
      .catch(() => {})
      .finally(() => setActivityLoading(false))
  }, [partyId])

  useEffect(() => {
    if (!partyId) return
    fetch(`/api/userimage/profile?userId=${encodeURIComponent(partyId)}`)
      .then((r) => r.json())
      .then((d) => setProfilePicUrl(d.url ?? null))
      .catch(() => {})
    fetch(`/api/userimage/brand?userId=${encodeURIComponent(partyId)}`)
      .then((r) => r.json())
      .then((d) => setBrandImageUrl(d.url ?? null))
      .catch(() => {})
    fetch(`/api/user-preferences/pdf_tagline?userId=${encodeURIComponent(partyId)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (d?.value) setTagline(d.value)
      })
      .catch(() => {})
    fetch(`/api/user-preferences/profile_pic_offset_y?userId=${encodeURIComponent(partyId)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (d?.value) setPicOffsetY(parseFloat(d.value))
      })
      .catch(() => {})
  }, [partyId])

  useEffect(() => {
    if (suburbQuery.length < 2) {
      setSuburbSuggestions([])
      return
    }
    fetchSuburbSuggestions(suburbQuery)
      .then((data) => setSuburbSuggestions(data.slice(0, 5)))
      .catch(() => setSuburbSuggestions([]))
  }, [suburbQuery])

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (suburbDropdownRef.current && !suburbDropdownRef.current.contains(e.target as Node)) {
        setSuburbDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleTaglineChange = (val: string) => {
    setTagline(val)
    if (taglineTimer.current) clearTimeout(taglineTimer.current)
    taglineTimer.current = setTimeout(async () => {
      if (!partyId) return
      setTaglineSaving(true)
      try {
        await fetch('/api/user-preferences', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            userId: partyId,
            preferenceKey: 'pdf_tagline',
            preferenceValue: val,
          }),
        })
      } catch {
        /* silent */
      } finally {
        setTaglineSaving(false)
      }
    }, 800)
  }

  const saveOffsetY = (offset: number) => {
    if (!partyId) return
    fetch('/api/user-preferences', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        userId: partyId,
        preferenceKey: 'profile_pic_offset_y',
        preferenceValue: String(offset),
      }),
    }).catch(() => {})
  }

  const handleAdjustPointerDown = (e: React.PointerEvent) => {
    dragStartY.current = e.clientY
    dragStartOffset.current = picOffsetY
    ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
  }

  const handleAdjustPointerMove = (e: React.PointerEvent) => {
    if (dragStartY.current === null) return
    const delta = e.clientY - dragStartY.current
    // drag up → more of bottom shown (offset increases), drag down → more of top (offset decreases)
    const next = Math.max(0, Math.min(100, dragStartOffset.current - delta * 0.8))
    setPicOffsetY(next)
  }

  const handleAdjustPointerUp = () => {
    if (dragStartY.current === null) return
    dragStartY.current = null
    saveOffsetY(picOffsetY)
  }

  const handleImageUpload = async (
    file: File,
    endpoint: string,
    setUrl: (url: string | null) => void,
    setUploading: (v: boolean) => void,
  ) => {
    if (!partyId) return
    setUploading(true)
    const form = new FormData()
    form.append('file', file)
    try {
      const res = await fetch(`/api/userimage/${endpoint}?userId=${encodeURIComponent(partyId)}`, {
        method: 'POST',
        body: form,
      })
      if (!res.ok) throw new Error('Upload failed')
      const d = await res.json()
      setUrl(d.url)
    } catch {
      // Silently ignore — user can retry
    } finally {
      setUploading(false)
    }
  }

  const handleDeleteImage = async (endpoint: string, setUrl: (url: string | null) => void) => {
    if (!partyId) return
    try {
      await fetch(`/api/userimage/${endpoint}?userId=${encodeURIComponent(partyId)}`, {
        method: 'DELETE',
      })
      setUrl(null)
    } catch {
      // Silently ignore
    }
  }

  const handleSignOut = () => {
    onSignOut?.()
    clearConfig()
    sessionStorage.clear()
    clearAppLocalStorage()
    instance.logoutRedirect({ account: instance.getActiveAccount() })
  }

  const togglePref = (key: 'suburb' | 'privacy') =>
    setOpenPref((prev) => (prev === key ? null : key))

  const displayName = profile?.fullName ?? username
  const nameForInitials = profile?.namePrefix
    ? displayName.replace(new RegExp(`^${profile.namePrefix}\\s+`, 'i'), '').trim()
    : displayName
  const initials = getInitials(nameForInitials)

  return (
    <div className="pb-4">
      <ServiceBanner banner={banner} />

      {/* ── Profile Header ───────────────────────────────────────────── */}
      <Card className="p-4 mb-6">
        <div className="flex items-center gap-4">
          {/* Tappable avatar — click to upload profile picture */}
          <input
            ref={profilePicInput}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0]
              if (file) handleImageUpload(file, 'profile', setProfilePicUrl, setUploadingPic)
              e.target.value = ''
            }}
          />
          <input
            ref={brandImageInput}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0]
              if (file) handleImageUpload(file, 'brand', setBrandImageUrl, setUploadingBrand)
              e.target.value = ''
            }}
          />
          <div className="relative shrink-0 flex flex-col items-center gap-1">
            {/* Avatar — tappable to upload when not adjusting, draggable when adjusting */}
            <div
              className={`relative w-16 h-16 rounded-full ${adjustingPic ? 'cursor-grab active:cursor-grabbing ring-2 ring-offset-1 ring-primary' : ''}`}
              onPointerDown={adjustingPic ? handleAdjustPointerDown : undefined}
              onPointerMove={adjustingPic ? handleAdjustPointerMove : undefined}
              onPointerUp={adjustingPic ? handleAdjustPointerUp : undefined}
            >
              {!adjustingPic && (
                <button
                  className="absolute inset-0 rounded-full z-10"
                  onClick={() => profilePicInput.current?.click()}
                  disabled={uploadingPic}
                />
              )}
              <Avatar className="w-16 h-16 border-2 border-primary">
                {profilePicUrl ? (
                  <img
                    src={profilePicUrl}
                    alt="Profile"
                    className="w-full h-full rounded-full object-cover"
                    style={{ objectPosition: `center ${picOffsetY}%` }}
                    draggable={false}
                  />
                ) : (
                  <AvatarFallback className="bg-primary text-primary-foreground text-xl font-semibold">
                    {initials}
                  </AvatarFallback>
                )}
              </Avatar>
              {!adjustingPic && (
                <span className="absolute bottom-0 right-0 w-5 h-5 bg-primary rounded-full flex items-center justify-center border-2 border-card pointer-events-none">
                  {uploadingPic ? (
                    <Loader2 className="w-2.5 h-2.5 text-primary-foreground animate-spin" />
                  ) : (
                    <Camera className="w-2.5 h-2.5 text-primary-foreground" />
                  )}
                </span>
              )}
            </div>

            {/* Adjust / Done / Delete row — only shown when photo is present */}
            {profilePicUrl && !uploadingPic && (
              <div className="flex gap-1">
                {adjustingPic ? (
                  <button
                    className="flex items-center gap-0.5 text-xs text-primary font-medium px-2 py-0.5 rounded-full bg-primary/10"
                    onClick={() => setAdjustingPic(false)}
                  >
                    <Check className="w-3 h-3" /> Done
                  </button>
                ) : (
                  <>
                    <button
                      className="flex items-center gap-0.5 text-xs text-muted-foreground px-2 py-0.5 rounded-full bg-muted"
                      onClick={() => setAdjustingPic(true)}
                    >
                      <ArrowUpDown className="w-3 h-3" /> Adjust
                    </button>
                    <button
                      className="flex items-center gap-0.5 text-xs text-destructive px-2 py-0.5 rounded-full bg-muted"
                      onClick={() => handleDeleteImage('profile', setProfilePicUrl)}
                    >
                      <Trash2 className="w-3 h-3" /> Remove
                    </button>
                  </>
                )}
              </div>
            )}
          </div>

          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-semibold text-foreground truncate font-heading">
              {displayName}
            </h2>
            {profile?.organization && (
              <p className="text-sm text-muted-foreground truncate">{profile.organization}</p>
            )}
            {profile?.primaryEmail && (
              <p className="text-sm text-muted-foreground truncate">{profile.primaryEmail}</p>
            )}
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon">
                <Settings className="w-5 h-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-40">
              <DropdownMenuItem
                onClick={handleSignOut}
                className="text-destructive focus:text-destructive"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <Separator className="my-4" />

        <div className="grid grid-cols-2 gap-4 text-center">
          <div>
            <p className="text-xs text-muted-foreground">Status</p>
            <Badge className="mt-1 bg-status-positive-light text-status-positive hover:bg-status-positive-light">
              {profile?.status ?? '—'}
            </Badge>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Member Since</p>
            <p className="text-sm font-semibold mt-1">
              {profile?.createdOn ? new Date(profile.createdOn).getFullYear() : '—'}
            </p>
          </div>
        </div>
      </Card>

      {/* ── PDF Tagline ──────────────────────────────────────────────── */}
      <Card className="p-4">
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-semibold">Hero Statement</h3>
          {taglineSaving && <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground" />}
        </div>
        <p className="text-xs text-muted-foreground mb-3">
          One-line header shown on every page of your shared reports.
        </p>
        <Input
          value={tagline}
          onChange={(e) => handleTaglineChange(e.target.value.slice(0, 100))}
          placeholder="e.g. Auckland's trusted real estate expert"
          maxLength={100}
        />
        <p className="text-xs text-muted-foreground mt-1 text-right">{tagline.length}/100</p>
      </Card>

      {/* ── Brand Image ──────────────────────────────────────────────── */}
      <BrandImageCard
        brandImageUrl={brandImageUrl}
        uploadingBrand={uploadingBrand}
        onUploadClick={() => brandImageInput.current?.click()}
        onDelete={() => handleDeleteImage('brand', setBrandImageUrl)}
      />

      {/* ── Section Tabs ─────────────────────────────────────────────── */}
      <div className="flex gap-2 mb-4">
        <Button
          variant={activeSection === 'activity' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setActiveSection('activity')}
          className="flex-1"
        >
          <Clock className="w-4 h-4 mr-2" />
          My Activity
        </Button>
        <Button
          variant={activeSection === 'profile' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setActiveSection('profile')}
          className="flex-1"
        >
          <User className="w-4 h-4 mr-2" />
          About
        </Button>
      </div>

      {/* ── My Activity Tab ───────────────────────────────────────────── */}
      {activeSection === 'activity' && (
        <div className="space-y-3">
          {activityLoading ? (
            <div className="flex justify-center py-10">
              <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <>
              {/* Saved Properties tile */}
              <button onClick={() => onNavigate?.('tools', 'saved')} className="w-full text-left">
                <Card className="p-4 hover:bg-muted/50 transition-colors cursor-pointer">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                      <Home className="w-6 h-6 text-primary" />
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-sm">Saved Properties</p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Your saved property estimates
                      </p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-2xl font-bold text-primary">{savedProperties.length}</p>
                      <p className="text-xs text-muted-foreground">saved</p>
                    </div>
                  </div>
                </Card>
              </button>

              {/* Saved Events tile */}
              <button onClick={() => onNavigate?.('events', 'saved')} className="w-full text-left">
                <Card className="p-4 hover:bg-muted/50 transition-colors cursor-pointer">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                      <CalendarDays className="w-6 h-6 text-primary" />
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-sm">Saved Events</p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Events you've bookmarked
                      </p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-2xl font-bold text-primary">{savedEventCount}</p>
                      <p className="text-xs text-muted-foreground">saved</p>
                    </div>
                  </div>
                </Card>
              </button>

              {/* Saved Education tile */}
              <button
                onClick={() => onNavigate?.('education', 'saved')}
                className="w-full text-left"
              >
                <Card className="p-4 hover:bg-muted/50 transition-colors cursor-pointer">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                      <GraduationCap className="w-6 h-6 text-primary" />
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-sm">Saved Education</p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Courses and resources saved
                      </p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-2xl font-bold text-primary">{savedEduCount}</p>
                      <p className="text-xs text-muted-foreground">saved</p>
                    </div>
                  </div>
                </Card>
              </button>

              {/* Recent property searches */}
              {savedProperties.length > 0 && (
                <Card className="p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Clock className="w-4 h-4 text-primary" />
                    <h3 className="font-semibold text-sm">Recent Estimates</h3>
                  </div>
                  <div className="space-y-2">
                    {savedProperties.slice(0, 5).map((p) => (
                      <div
                        key={p.rafid}
                        className="flex items-center gap-3 py-2 border-b border-border last:border-0"
                      >
                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                          <Home className="w-4 h-4 text-primary" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{p.address}</p>
                          <p className="text-xs text-muted-foreground">
                            {new Intl.NumberFormat('en-NZ', {
                              style: 'currency',
                              currency: 'NZD',
                              maximumFractionDigits: 0,
                            }).format(p.low)}
                            {' – '}
                            {new Intl.NumberFormat('en-NZ', {
                              style: 'currency',
                              currency: 'NZD',
                              maximumFractionDigits: 0,
                            }).format(p.high)}
                          </p>
                        </div>
                        <p className="text-xs text-muted-foreground shrink-0">
                          {new Date(p.savedAt).toLocaleDateString('en-NZ', {
                            day: 'numeric',
                            month: 'short',
                          })}
                        </p>
                      </div>
                    ))}
                  </div>
                </Card>
              )}
            </>
          )}
        </div>
      )}

      {/* ── Profile Tab ──────────────────────────────────────────────── */}
      {activeSection === 'profile' && (
        <div className="space-y-4">
          {loading && (
            <Card className="p-8 text-center">
              <p className="text-sm text-muted-foreground">Loading profile…</p>
            </Card>
          )}

          {error && (
            <Card className="p-4 border-destructive">
              <p className="text-sm text-destructive">{error}</p>
            </Card>
          )}

          {!loading && profile && (
            <>
              <Card className="p-4">
                <h3 className="font-semibold mb-4">Contact Details</h3>
                <div className="space-y-3">
                  <div className="flex items-start gap-3 py-2 border-b border-border">
                    <User className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
                    <div>
                      <p className="text-xs text-muted-foreground">Full Name</p>
                      <p className="text-sm font-medium">{profile.fullName}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 py-2 border-b border-border">
                    <Building2 className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
                    <div>
                      <p className="text-xs text-muted-foreground">Organisation</p>
                      <p className="text-sm font-medium">{profile.organization}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 py-2 border-b border-border">
                    <Mail className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
                    <div>
                      <p className="text-xs text-muted-foreground">Email</p>
                      <p className="text-sm font-medium">{profile.primaryEmail}</p>
                    </div>
                  </div>
                  {profile.workPhone && (
                    <div className="flex items-start gap-3 py-2 border-b border-border">
                      <Phone className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
                      <div>
                        <p className="text-xs text-muted-foreground">Work Phone</p>
                        <p className="text-sm font-medium">{profile.workPhone}</p>
                      </div>
                    </div>
                  )}
                  {profile.mobilePhone && (
                    <div className="flex items-start gap-3 py-2">
                      <Phone className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
                      <div>
                        <p className="text-xs text-muted-foreground">Mobile</p>
                        <p className="text-sm font-medium">{profile.mobilePhone}</p>
                      </div>
                    </div>
                  )}
                </div>
              </Card>

              <Card className="p-4">
                <h3 className="font-semibold mb-4">Addresses</h3>
                <div className="space-y-3">
                  {profile.physicalFullAddress && (
                    <div className="flex items-start gap-3 py-2 border-b border-border">
                      <MapPin className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-muted-foreground">Physical Address</p>
                        <p className="text-sm font-medium line-clamp-2">
                          {formatAddress(profile.physicalFullAddress)}
                        </p>
                      </div>
                      <a
                        href={getMapsUrl(profile.physicalFullAddress)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 text-xs text-primary hover:underline shrink-0 mt-0.5"
                      >
                        <ExternalLink className="w-3 h-3" />
                        Maps
                      </a>
                    </div>
                  )}
                  {profile.postalFullAddress && (
                    <div className="flex items-start gap-3 py-2">
                      <MapPin className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
                      <div className="min-w-0">
                        <p className="text-xs text-muted-foreground">Postal Address</p>
                        <p className="text-sm font-medium line-clamp-2">
                          {formatAddress(profile.postalFullAddress)}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </Card>
            </>
          )}

          <Card className="p-4">
            <h3 className="font-semibold mb-4">Preferences</h3>
            <div className="space-y-1">
              <button
                className="w-full flex items-center justify-between py-3 text-left hover:text-primary transition-colors"
                onClick={() => togglePref('suburb')}
              >
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm">Default Suburb</span>
                </div>
                {openPref === 'suburb' ? (
                  <ChevronDown className="w-4 h-4 text-muted-foreground" />
                ) : (
                  <ChevronRight className="w-4 h-4 text-muted-foreground" />
                )}
              </button>
              {openPref === 'suburb' && (
                <div className="pl-6 pb-3 border-b border-border space-y-2" ref={suburbDropdownRef}>
                  <p className="text-xs text-muted-foreground">
                    Pre-selects your suburb in Market Insights. Auto-detected from your iMIS
                    address.
                  </p>
                  <button
                    onClick={() => {
                      setSuburbDropdownOpen((o) => !o)
                      setSuburbQuery('')
                      setSuburbSuggestions([])
                    }}
                    className="flex items-center justify-between w-full text-sm font-medium border border-border rounded-md px-3 py-2 hover:bg-muted transition-colors"
                  >
                    <span>{defaultSuburb ? defaultSuburb.suburbName : 'Select suburb'}</span>
                    <ChevronDown className="w-4 h-4 text-muted-foreground" />
                  </button>
                  {suburbDropdownOpen && (
                    <div className="bg-background border border-border rounded-lg shadow-lg overflow-hidden">
                      <div className="p-2 border-b border-border">
                        <div className="relative">
                          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground" />
                          <input
                            autoFocus
                            value={suburbQuery}
                            onChange={(e) => setSuburbQuery(e.target.value)}
                            placeholder="Search suburbs..."
                            className="w-full pl-6 pr-2 py-1 text-xs border rounded bg-background text-foreground focus:outline-hidden focus:ring-1 focus:ring-primary"
                          />
                        </div>
                      </div>
                      <div>
                        {suburbSuggestions.length > 0 ? (
                          suburbSuggestions.map((s) => (
                            <button
                              key={s.suburbID}
                              className={`w-full text-left px-3 py-2 text-sm hover:bg-muted transition-colors flex items-center gap-2 ${defaultSuburb?.suburbID === s.suburbID ? 'bg-primary/10 text-primary font-medium' : ''}`}
                              onClick={() => {
                                setDefaultSuburb(s)
                                setSuburbDropdownOpen(false)
                                setSuburbQuery('')
                                if (partyId)
                                  savePreference(partyId, 'reinz_pinned_suburb', JSON.stringify(s))
                              }}
                            >
                              {defaultSuburb?.suburbID === s.suburbID && (
                                <Check className="w-3 h-3 shrink-0" />
                              )}
                              <span className="truncate">{s.suburbName}</span>
                              <span className="text-xs text-muted-foreground ml-auto shrink-0">
                                {s.taName?.replace(' District', '').replace(' City', '') ?? ''}
                              </span>
                            </button>
                          ))
                        ) : suburbQuery.length < 2 ? (
                          <p className="px-3 py-2 text-xs text-muted-foreground">
                            Type to search suburbs...
                          </p>
                        ) : (
                          <p className="px-3 py-2 text-xs text-muted-foreground">
                            No results found
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                  {defaultSuburb && !suburbDropdownOpen && (
                    <p className="text-xs text-muted-foreground">
                      Current:{' '}
                      <span className="font-medium text-foreground">
                        {defaultSuburb.suburbName}
                        {defaultSuburb.taName
                          ? `, ${defaultSuburb.taName.replace(' District', '').replace(' City', '')}`
                          : ''}
                      </span>
                    </p>
                  )}
                </div>
              )}

              <button
                className="w-full flex items-center justify-between py-3 text-left hover:text-primary transition-colors"
                onClick={() => togglePref('privacy')}
              >
                <div className="flex items-center gap-2">
                  <Shield className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm">Data & Privacy</span>
                </div>
                {openPref === 'privacy' ? (
                  <ChevronDown className="w-4 h-4 text-muted-foreground" />
                ) : (
                  <ChevronRight className="w-4 h-4 text-muted-foreground" />
                )}
              </button>
              {openPref === 'privacy' && (
                <div className="pl-6 pb-3 space-y-2">
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    Your profile data is sourced via API from your REINZ-managed iMIS account. All
                    personal information collected by REINZ via the app is processed in accordance
                    with our privacy policy.
                  </p>
                  <a
                    href="https://www.reinz.co.nz/privacy-policy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-xs text-primary hover:underline"
                  >
                    <ExternalLink className="w-3 h-3" />
                    REINZ Privacy Policy
                  </a>
                </div>
              )}
            </div>
          </Card>

          <Button
            variant="outline"
            className="w-full text-destructive hover:text-destructive"
            onClick={handleSignOut}
          >
            <LogOut className="w-4 h-4 mr-2" />
            Sign Out
          </Button>
        </div>
      )}

      <PoweredByFooter />
    </div>
  )
}
